import { BarChart3, Clock, Eye, ListChecks, ListVideo, RefreshCw, Scale, Users } from 'lucide-react'
import { useMemo, useState } from 'react'
import { useNavigate, useSearch } from '@tanstack/react-router'
import { toast } from 'sonner'

import {
  useChannelStats,
  useChannelVideoStats,
  useRefreshUploadStats,
  useStatsSync,
  useUploadStatus,
} from '@/api/endpoints/youtube/uploads'
import { useExternalUploads } from '@/api/endpoints/analytics'
import { useRankings } from '@/api/endpoints/ranking'
import { useVideos, type VideoItem } from '@/api/endpoints/videos'
import { ANALYTICS_TABS, type AnalyticsTab } from '@/app/routes/_search'
import { EmptyState } from '@/components/ds/EmptyState'
import { PageHeader } from '@/components/ds/PageHeader'
import { Section } from '@/components/ds/Section'
import { Button } from '@/components/ui/Button'
import { Pagination } from '@/components/ui/Pagination'
import { readRememberedTab, TabsContent, TabsList, TabsRoot, TabsTrigger } from '@/components/ui/Tabs'
import { ChannelCompareSection } from '@/features/analytics/components/ChannelCompareSection'
import { CorrelationSection } from '@/features/analytics/components/CorrelationSection'
import { PeakHoursSection } from '@/features/analytics/components/PeakHoursSection'
import { RetentionAuditSection } from '@/features/analytics/components/RetentionAuditSection'
import { KPIGrid } from '@/features/analytics/shared/KPIGrid'
import { nextSort, type SortState } from '@/features/analytics/shared/sort'
import {
  numberByPublication,
  sortUploadRows,
  type UploadRow,
  type UploadSortCol,
} from '@/features/analytics/shared/uploadRows'
import { UploadsTable, UploadsTableSkeleton } from '@/features/analytics/shared/UploadsTable'
import { useRelativeTime } from '@/hooks/useRelativeTime'
import { errorMessage } from '@/lib/errors'
import { formatRelativeDate } from '@/lib/format'
import type { Channel } from '@/api/endpoints/channels'
import styles from '@/features/analytics/pages/AnalyticsPage/AnalyticsPage.module.scss'
import { QueryErrorNotice } from '@/components/feedback/QueryErrorNotice'

interface YoutubeAnalyticsPageProps {
  channel: Channel
}

/** The tab a bare ``/analytics`` opens when the user never picked one. */
const DEFAULT_TAB: AnalyticsTab = 'uploads'

/** Names this page's tabs: the last one picked, and their dragged order. */
const TABS_KEY = 'youtube-analytics'

/** What ``/uploads/video-stats`` returns per YouTube id (``extra='allow'``). */
interface LiveVideoStats {
  views?: number
  likes?: number
  comments?: number
  title?: string
}

/**
 * YouTube analytics dashboard — channel KPIs + published videos + retention
 * audit + peak hours + correlation + cross-channel comparison. Owns the URL
 * search params (tab, page, size) that the route validates. ``AnalyticsPage``
 * (the dispatcher) routes here when ``channel.platform === 'youtube'``.
 */
export function YoutubeAnalyticsPage({ channel }: YoutubeAnalyticsPageProps) {
  const channelSlug = channel.slug
  const channelStats = useChannelStats(channelSlug, true)
  const uploadStatus = useUploadStatus(channelSlug)
  const videos = useVideos({ channel: channelSlug, page: 1, limit: 200 })
  const videoStats = useChannelVideoStats(channelSlug, true)
  // External uploads (manual/non-Prism YT videos on the same channel) —
  // Sub-phase 1 of the /analytics-external item. Merged into the
  // published-videos list below.
  const externalUploads = useExternalUploads(channelSlug)
  // Ranking grade per published video. The endpoint caps at 1000 — above the
  // 172 uploads of the biggest channel today, so one page covers the table.
  const rankings = useRankings(channelSlug, 1000)

  // ``from`` types ``prev`` below as this route's search — /settings also has
  // a ``tab``, and the union of both wouldn't fit either route.
  const navigate = useNavigate({ from: '/analytics' })
  const search = useSearch({ from: '/_app/analytics' })
  // Sem ``?tab=``, a última aba escolhida neste navegador.
  const activeTab: AnalyticsTab = search.tab ?? readRememberedTab(TABS_KEY, ANALYTICS_TABS) ?? DEFAULT_TAB
  const page = search.page ?? 1
  const pageSize = search.size ?? 25
  function patchSearch(patch: { tab?: AnalyticsTab; page?: number; size?: number }): void {
    void navigate({
      to: '/analytics',
      search: (prev) => {
        const next = { ...prev, ...patch }
        if (next.tab === DEFAULT_TAB) delete next.tab
        if (next.page === 1) delete next.page
        if (next.size === 25) delete next.size
        return next
      },
    })
  }

  // Every video the channel has on YouTube — Prism uploads plus the external
  // ones — newest first. ``seq`` numbers them in publication order.
  const published = useMemo<UploadRow[]>(() => {
    const uploads = uploadStatus.data?.uploads ?? []
    const videoMap = new Map<string, VideoItem>()
    for (const v of videos.data?.videos ?? []) {
      videoMap.set(`${v.id}-${v.language}`, v)
    }
    const stats = (videoStats.data ?? {}) as Record<string, LiveVideoStats | undefined>
    const rankingMap = new Map(
      (rankings.data ?? []).map((r) => [`${r.video_id}-${r.language}`, r] as const),
    )
    const prismRows: UploadRow[] = uploads
      .filter((u) => u.status === 'published' && u.youtube_video_id)
      .map((u) => {
        const v = videoMap.get(`${u.video_id}-${u.language}`)
        const ytId = u.youtube_video_id || ''
        const s = stats[ytId]
        const rk = rankingMap.get(`${u.video_id}-${u.language}`)
        return {
          videoId: u.video_id,
          language: u.language,
          externalId: ytId,
          watchUrl: `https://www.youtube.com/shorts/${ytId}`,
          // The project list only covers the 200 newest projects; older uploads
          // fall back to the title YouTube reports, then to the one sent at upload.
          title: v?.metadata?.title || v?.meta_title || s?.title || stringField(u.title) || u.video_id,
          views: s?.views ?? null,
          likes: s?.likes ?? null,
          comments: s?.comments ?? null,
          publishedAt: u.uploaded_at ?? null,
          scheduledFor: stringField(u.scheduled_for) || null,
          score: rk?.final_score ?? null,
          criteriaVersion: rk?.criteria_version ?? null,
        }
      })
    // External uploads come from the /analytics/external-uploads endpoint.
    // They share the same shape but don't link into a project page (videoId
    // is empty) and carry the YT URL + thumbnail instead.
    const externalRows: UploadRow[] = (externalUploads.data?.items ?? []).map((e) => ({
      videoId: '',
      language: '',
      externalId: e.external_id,
      title: e.title || e.external_id,
      views: e.view_count,
      likes: e.like_count,
      comments: e.comment_count,
      isExternal: true,
      externalUrl: `https://www.youtube.com/watch?v=${e.external_id}`,
      thumbnailUrl: e.thumbnail_url,
      publishedAt: e.published_at ?? null,
    }))
    return numberByPublication([...prismRows, ...externalRows])
  }, [uploadStatus.data, videos.data, videoStats.data, externalUploads.data, rankings.data])

  // null = default order (newest first). Local state like the retention
  // table's sort — only tab / page / size ride in the URL.
  const [uploadsSort, setUploadsSort] = useState<SortState<UploadSortCol> | null>(null)
  const sortedPublished = useMemo(() => sortUploadRows(published, uploadsSort), [published, uploadsSort])

  return (
    <div className={styles.page}>
      <Header channel={channel.name} actions={<RefreshStatsButton channelSlug={channelSlug} />} />

      <KPIGrid
        loading={channelStats.isPending}
        error={channelStats.error}
        onRetry={() => {
          void channelStats.refetch()
        }}
        items={[
          {
            icon: <Users />,
            label: 'Inscritos',
            value: channelStats.data?.subscriber_count,
            hint: 'YouTube · snapshot atual',
          },
          {
            icon: <Eye />,
            label: 'Views totais',
            value: channelStats.data?.view_count,
            hint: 'desde o início do canal',
          },
          {
            icon: <BarChart3 />,
            label: 'Vídeos no canal',
            value: channelStats.data?.video_count,
            hint: `${String(videos.data?.videos.length ?? 0)} no Prism`,
          },
        ]}
      />

      <TabsRoot
        persistKey={TABS_KEY}
        value={activeTab}
        onValueChange={(value) => {
          const next = value as AnalyticsTab
          const paginated = next === 'uploads' || next === 'retention'
          patchSearch(paginated ? { tab: next } : { tab: next, page: 1 })
        }}
      >
        <TabsList aria-label="Seções de analytics">
          <TabsTrigger value="uploads" leadingIcon={<ListVideo />}>Vídeos publicados</TabsTrigger>
          <TabsTrigger value="retention" leadingIcon={<ListChecks />}>Retenção</TabsTrigger>
          <TabsTrigger value="hours" leadingIcon={<Clock />}>Horários</TabsTrigger>
          <TabsTrigger value="correlation" leadingIcon={<BarChart3 />}>Correlação</TabsTrigger>
          <TabsTrigger value="compare" leadingIcon={<Scale />}>Comparativo</TabsTrigger>
        </TabsList>

        <TabsContent value="uploads" className={styles.tabContent}>
          <Section
            title="Vídeos publicados"
            description={
              uploadStatus.isPending
                ? 'Carregando...'
                : `${String(published.length)} vídeo${published.length === 1 ? '' : 's'} publicado${published.length === 1 ? '' : 's'}`
            }
          >
            {uploadStatus.isError ? (
              <QueryErrorNotice
                error={uploadStatus.error}
                label="os vídeos publicados"
                onRetry={() => {
                  void uploadStatus.refetch()
                }}
              />
            ) : uploadStatus.isPending ? (
              <UploadsTableSkeleton idHeader="ID" showScore />
            ) : published.length === 0 ? (
              <EmptyState
                icon={<BarChart3 />}
                title="Nenhum upload publicado"
                description="Quando você publicar vídeos do projeto via aba Publicar, eles aparecem aqui."
              />
            ) : (
              <>
                <UploadsTable
                  rows={sortedPublished.slice((page - 1) * pageSize, page * pageSize)}
                  startIdx={(page - 1) * pageSize}
                  statsLoading={videoStats.isPending}
                  showScore
                  scoreLoading={rankings.isPending}
                  sort={uploadsSort}
                  onToggleSort={(col) => {
                    setUploadsSort((prev) => nextSort(prev, col))
                    // A new order starts from its top. Guarded because every
                    // navigation cross-fades the page (defaultViewTransition).
                    if (page !== 1) patchSearch({ page: 1 })
                  }}
                />
                <Pagination
                  page={page}
                  pageSize={pageSize}
                  total={published.length}
                  onPageChange={(next) => {
                    patchSearch({ page: next })
                  }}
                  onPageSizeChange={(next) => {
                    patchSearch({ size: next, page: 1 })
                  }}
                  itemLabel="vídeo"
                  itemLabelPlural="vídeos"
                />
              </>
            )}
          </Section>
        </TabsContent>

        <TabsContent value="retention" className={styles.tabContent}>
          <RetentionAuditSection
            channel={channelSlug}
            page={page}
            pageSize={pageSize}
            onPageChange={(next) => {
              patchSearch({ page: next })
            }}
            onPageSizeChange={(next) => {
              patchSearch({ size: next, page: 1 })
            }}
          />
        </TabsContent>

        <TabsContent value="hours" className={styles.tabContent}>
          <PeakHoursSection channel={channelSlug} />
        </TabsContent>

        <TabsContent value="correlation" className={styles.tabContent}>
          <CorrelationSection channel={channelSlug} />
        </TabsContent>

        <TabsContent value="compare" className={styles.tabContent}>
          {/* Cross-channel: ignores the active ``channel`` — compares every
              YouTube channel via the aggregated /analytics/compare endpoint. */}
          <ChannelCompareSection platform="youtube" />
        </TabsContent>
      </TabsRoot>
    </div>
  )
}

function Header({ channel, actions }: { channel: string; actions?: React.ReactNode }) {
  return (
    <PageHeader
      eyebrow="Visão do canal"
      title="Analytics"
      description={`Snapshot do canal ${channel}. Charts de série temporal chegam quando expusermos o histórico.`}
      actions={actions}
    />
  )
}

function RefreshStatsButton({ channelSlug }: { channelSlug: string }) {
  const refresh = useRefreshUploadStats()
  return (
    <>
      <LastSyncLabel channelSlug={channelSlug} />
      <Button
        variant="secondary"
        size="sm"
        leadingIcon={<RefreshCw className={refresh.isPending ? styles.spinIcon : undefined} />}
        disabled={refresh.isPending}
        onClick={() => {
          refresh.mutate(channelSlug, {
            onSuccess: (data) => {
              toast.success(
                `Stats atualizados — ${String(data.videos_synced)} vídeo${data.videos_synced === 1 ? '' : 's'} sincronizado${data.videos_synced === 1 ? '' : 's'}`,
              )
            },
            onError: (err) => {
              toast.error(errorMessage(err, 'Falha ao atualizar stats'))
            },
          })
        }}
      >
        {refresh.isPending ? 'Atualizando...' : 'Atualizar agora'}
      </Button>
    </>
  )
}

/**
 * When Prism last synced this channel's counters — the hourly cron or the
 * button next to it, whichever ran last. Exact time on hover. Renders
 * nothing while loading or on error: it's a hint, the page works without it.
 */
function LastSyncLabel({ channelSlug }: { channelSlug: string }) {
  const sync = useStatsSync(channelSlug)
  const lastSyncedAt = sync.data?.last_synced_at ?? null
  const relative = useRelativeTime(lastSyncedAt)
  if (!sync.data) return null
  if (!lastSyncedAt) return <span className={styles.lastSync}>Ainda não atualizado</span>
  return (
    <span className={styles.lastSync} title={formatRelativeDate(lastSyncedAt)}>
      Última atualização: <time dateTime={lastSyncedAt}>{relative}</time>
    </span>
  )
}

function stringField(value: unknown): string {
  return typeof value === 'string' ? value : ''
}
